import { useAtomValue } from 'jotai';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import type { FilterKey } from '@core/Entities/Search/QueryParams.entity';
import { getFilterFromArray } from '@core/Utils/SearchFilters/GetFilterFromArray';
import SelectionControls from '@gds/SelectionControls/SelectionControls';
import { flagsAtom } from '@growthBookExperimentation/Atoms/Flags.atom';
import { useUpdateFilters } from '@search-filters/Hooks/UseUpdateFilters';

import type { FilterControlBaseProps } from '../../FilterPanel.entity';
import { trackFilterEvent } from '../../Filters.tracking';
import { getEnabledToggleConfig } from './ShowOnly.utils';

import styles from './ShowOnly.module.css';

type ShowOnlyProps = Pick<FilterControlBaseProps, 'strings' | 'tenant'>;

export const ShowOnly = ({ strings, tenant }: ShowOnlyProps) => {
  const filtersList = useAtomValue(filtersListAtom);
  const flags = useAtomValue(flagsAtom);
  const { updateFilters } = useUpdateFilters();

  const enabledToggles = getEnabledToggleConfig({
    tenant,
    flags,
  });

  const filterConfigs = [
    {
      id: 'ecom',
      isVisible: enabledToggles.ecom,
      isChecked: getFilterFromArray('ecom', filtersList)?.value === 'true',
      label: strings.ecom,
      description: strings.ecomDesc,
      checkedValue: 'true',
    },
    {
      id: 'eligible-products',
      isVisible: enabledToggles.eligibleProductsUk,
      isChecked: getFilterFromArray('eligible-products', filtersList)?.value === 'ONLY_ON_HEYCAR',
      label: tenant ? strings.d2cRac : strings.d2c,
      description: tenant ? strings.d2cRacDesc : strings.d2cDesc,
      checkedValue: 'ONLY_ON_HEYCAR',
    },
    {
      id: 'partner-rac',
      isVisible: enabledToggles.partnerRac,
      isChecked: getFilterFromArray('partner-rac', filtersList)?.value === 'true',
      label: strings.rac,
      description: strings.racDesc,
      checkedValue: 'true',
    },
  ];

  const handleChange = (
    filterId: FilterKey,
    eventTarget: EventTarget & HTMLInputElement,
    checkedValue: string,
  ) => {
    const value = eventTarget.checked ? checkedValue : undefined;
    trackFilterEvent(filterId, value || 'false', filtersList);
    updateFilters([{ id: filterId, value }]);
  };

  return (
    <section className={styles.showOnly}>
      {filterConfigs.map(
        ({ id, isVisible, isChecked, label, description, checkedValue }) =>
          isVisible && (
            <SelectionControls
              key={id}
              checked={isChecked}
              description={description}
              label={label}
              onChange={e =>
                handleChange(
                  id as FilterKey,
                  e.target as EventTarget & HTMLInputElement,
                  checkedValue,
                )
              }
              type="switch"
            />
          ),
      )}
    </section>
  );
};
